import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronRight } from 'react-icons/fi';
import Button from '../common/Button';
import { Icon } from '../common/Icon';
import { ROUTES } from '../../constants/constants';
import { companiesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';

/**
 * "Your request to join a client's company is still with the owner" — on the
 * dashboard a professional actually lands on.
 *
 * The request itself has always lived on the Collaboration Hub page under a
 * "Requested" filter. Nobody found it there: after adding a client's CIN, the
 * professional came back to this dashboard, saw the company absent from the
 * portfolio, and could not tell whether the request was pending, approved or
 * lost (Ritik, 21 Sep 2026). So the pending ones — and anything approved in
 * the last week — are said out loud here, with the two things they can do:
 * nudge the owner, or open the full list.
 *
 * Quiet on failure: a banner is never the thing that errors a dashboard.
 */
const RECENT_DAYS = 7;

export default function AccessRequestsBanner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useUI();
  const [pending, setPending] = useState([]);
  const [recentlyApproved, setRecentlyApproved] = useState([]);
  const [reminding, setReminding] = useState('');

  const isProfessional = String(user?.roleName || '').toLowerCase() === 'professional';

  const load = useCallback(async () => {
    if (!isProfessional) return;
    try {
      const res = await companiesAPI.access.myRequests();
      const rows = Array.isArray(res?.data) ? res.data : [];
      const cutoff = Date.now() - RECENT_DAYS * 24 * 3600 * 1000;
      setPending(rows.filter((r) => r.status === 'pending' && r.company));
      setRecentlyApproved(rows.filter((r) => r.status === 'active' && r.grantedAt && new Date(r.grantedAt).getTime() >= cutoff && r.company));
    } catch {
      setPending([]);
      setRecentlyApproved([]);
    }
  }, [isProfessional]);

  useEffect(() => { load(); }, [load]);

  if (!isProfessional || (!pending.length && !recentlyApproved.length)) return null;

  const openList = () => navigate(`${ROUTES.PROFESSIONAL_DASHBOARD}?tab=requests`);

  const remind = async (r) => {
    setReminding(r._id);
    try {
      const res = await companiesAPI.access.remind(r.company._id, r._id);
      showToast(res?.message || `Reminder sent to ${r.owner?.fullName || 'the owner'}.`, 'success');
      load();
    } catch (error) {
      showToast(error?.message || 'Could not send the reminder.', 'error');
    } finally {
      setReminding('');
    }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '');
  const one = pending.length === 1;
  const first = pending[0];

  return (
    <>
      {pending.length > 0 && (
        <div
          className="flex items-center gap-3.5 px-4 py-3 mb-3 rounded-[12px] border"
          style={{ background: '#faf6eb', borderColor: '#ebdcb9', boxShadow: '0 2px 8px rgba(122,112,96,0.02)' }}
          role="status"
        >
          <Icon name="pending" size={18} style={{ color: '#8a6a1f' }} />
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-bold" style={{ color: '#8a6a1f' }}>
              {one
                ? `Access to ${first.company.name} is awaiting the owner's approval`
                : `${pending.length} access requests are awaiting the owners' approval`}
            </div>
            <div className="text-[10.5px] text-[#7a7060] mt-[1px]">
              {one
                ? <>Sent {fmt(first.requestedAt)} to {first.owner?.fullName || first.owner?.email || 'the company owner'}. The company appears in your portfolio once they approve.</>
                : <>{pending.map((r) => r.company.name).join(', ')}. Each appears in your portfolio once its owner approves.</>}
            </div>
          </div>
          {one && (
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 whitespace-nowrap !py-1 !px-2.5 text-[11px] font-semibold hover:!bg-[#8a6a1f]/[0.08] hover:!text-[#8a6a1f]"
              onClick={() => remind(first)}
              loading={reminding === first._id}
            >
              Remind owner
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="group shrink-0 whitespace-nowrap !py-1 !px-2.5 text-[11px] font-semibold hover:!bg-[#8a6a1f]/[0.08] hover:!text-[#8a6a1f]"
            rightIcon={<FiChevronRight size={12} className="transition-transform duration-200 group-hover:translate-x-0.5" />}
            onClick={openList}
          >
            <span className="transition-transform duration-200 group-hover:translate-x-0.5">View requests</span>
          </Button>
        </div>
      )}

      {recentlyApproved.length > 0 && (
        <div
          className="flex items-center gap-3.5 px-4 py-3 mb-3 rounded-[12px] border"
          style={{ background: '#f1f6f0', borderColor: '#cfe0cb' }}
          role="status"
        >
          <Icon name="success" size={18} style={{ color: '#4a6b3f' }} />
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-bold" style={{ color: '#4a6b3f' }}>
              {recentlyApproved.length === 1
                ? `${recentlyApproved[0].company.name} approved your access`
                : `${recentlyApproved.length} companies approved your access this week`}
            </div>
            <div className="text-[10.5px] text-[#7a7060] mt-[1px]">
              {recentlyApproved.map((r) => `${r.company.name} (${fmt(r.grantedAt)})`).join(', ')} — now in your portfolio.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
